import { useState, useRef, useCallback, useEffect } from "react";
import axiosInstance from "../../../../axiosInstance";

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

/**
 * Shared debounced search hook for component search bars.
 * @param {string} searchEndpoint - The full URL to call (query and company_id appended as params)
 * @param {string} companyId - The company UUID
 */
const useComponentSearch = (searchEndpoint, companyId) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const search = useCallback(
    (searchQuery) => {
      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (!searchQuery || searchQuery.length < MIN_QUERY_LENGTH) {
        setResults([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const controller = new AbortController();
      abortControllerRef.current = controller;

      axiosInstance
        .get(searchEndpoint, {
          params: { q: searchQuery, company_id: companyId },
          signal: controller.signal,
        })
        .then((response) => {
          setResults(response.data?.items || []);
          setIsLoading(false);
        })
        .catch((err) => {
          if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
            setIsLoading(false);
            setResults([]);
          }
        });
    },
    [searchEndpoint, companyId]
  );

  const handleQueryChange = useCallback(
    (value) => {
      setQuery(value);
      clearTimeout(debounceTimerRef.current);

      if (!value || value.length < MIN_QUERY_LENGTH) {
        setResults([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      debounceTimerRef.current = setTimeout(() => {
        search(value);
      }, DEBOUNCE_MS);
    },
    [search]
  );

  const clearSearch = useCallback(() => {
    setQuery("");
    setResults([]);
    setIsLoading(false);
    clearTimeout(debounceTimerRef.current);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeout(debounceTimerRef.current);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return { query, results, isLoading, handleQueryChange, clearSearch };
};

export default useComponentSearch;
